import { Elements } from '@stripe/react-stripe-js'
import { Button } from '@viastud/ui/button'
import { Card } from '@viastud/ui/card'
import { Dialog, DialogClose, DialogContent, DialogHeader, DialogTitle } from '@viastud/ui/dialog'
import { useToast } from '@viastud/ui/hooks/use-toast'
import { trpc } from '@viastud/ui/lib/trpc'
import { Minus, Plus, X } from 'lucide-react'
import { useState } from 'react'

import PaymentForm from '@/components/payment-form.tsx'
import { getStripe } from '@/lib/stripe'

export function UnitaryCourseCard({
  userGrade,
  hasSubscription,
}: {
  hasSubscription: boolean
  userGrade: string
}) {
  const UNITARY_PRICE = userGrade === 'TERMINALE' || userGrade === 'PREMIERE' ? 29.99 : 19.99

  const { toast } = useToast()
  const { mutateAsync: payForIndividualLessons } =
    trpc.payment.createSingleLessonPaymentIntent.useMutation()
  const { mutateAsync: cancelPaymentIntentMutation } =
    trpc.payment.cancelPaymentIntent.useMutation()

  const [quantity, setQuantity] = useState(1)
  const [isProcessing, setIsProcessing] = useState(false)
  const [isIndividualPaymentOpen, setIsIndividualPaymentOpen] = useState(false)
  const [individualPaymentClientSecret, setIndividualClientSecret] = useState<string | null>(null)

  const formatPrice = () => (UNITARY_PRICE * quantity).toFixed(2).replace('.', ',')
  const handlePayForIndividualLessons = async () => {
    try {
      const { clientSecret } = await payForIndividualLessons({
        quantity,
      })
      setIndividualClientSecret(clientSecret)
      setIsIndividualPaymentOpen(true)
    } catch (error) {
      if (error instanceof Error) {
        toast({
          title: 'Erreur de paiement',
          description:
            error.message ?? 'Une erreur est survenue lors du paiement. Veuillez réessayer.',
          variant: 'destructive',
        })
      }
    }
  }

  const handleCancelPayment = async () => {
    try {
      await cancelPaymentIntentMutation()
      setIsIndividualPaymentOpen(false)
    } catch (error) {
      if (error instanceof Error) {
        toast({
          title: 'Erreur de paiement',
          description: error.message ?? "Une erreur est survenue lors de l'annulation.",
          variant: 'destructive',
        })
      }
    }
  }

  return (
    <>
      <Card className="rounded-2xl bg-white px-6 py-3">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h4 className="text-lg font-semibold">Cours à l’unité</h4>
            <p className="mb-3 text-sm text-gray-500">Achetez plusieurs cours.</p>
          </div>
          <span className="self-start text-4xl font-bold">{formatPrice()}€</span>
        </div>

        <div className="flex items-center gap-x-6">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-3">
              <Button
                disabled={hasSubscription}
                variant="secondary"
                className="border border-gray-200 px-2"
                onClick={() => {
                  if (quantity > 1) {
                    setQuantity((q) => q - 1)
                  }
                }}
              >
                <Minus />
              </Button>
              <span className="flex-1 text-center text-4xl font-bold text-blue-600 sm:text-6xl">
                {quantity.toString().padStart(2, '0')}
              </span>
              <Button
                disabled={hasSubscription}
                className="px-2"
                onClick={() => {
                  setQuantity((q) => q + 1)
                }}
              >
                <Plus />
              </Button>
            </div>
          </div>
          <Button
            className="flex-1"
            onClick={async () => {
              setIsProcessing(true)
              await handlePayForIndividualLessons()
              setIsProcessing(false)
            }}
            disabled={isProcessing || hasSubscription}
          >
            {isProcessing ? 'Chargement...' : `Acheter ${quantity} cours`}
          </Button>
        </div>
      </Card>
      {isIndividualPaymentOpen && (
        <PaymentModal
          isOpen={isIndividualPaymentOpen}
          onOpenChange={setIsIndividualPaymentOpen}
          clientSecret={individualPaymentClientSecret}
          onClose={() => {
            void handleCancelPayment()
          }}
          title={`Acheter un cours pour ${String(UNITARY_PRICE).replace('.', ',')}€`}
          returnUrl={window.location.href}
        />
      )}
    </>
  )
}

function PaymentModal({
  isOpen,
  onOpenChange,
  clientSecret,
  onClose,
  title,
  returnUrl,
}: {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  clientSecret: string | null
  onClose: () => void
  returnUrl: string
  title: string
}) {
  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="flex flex-col gap-8">
        <DialogHeader className="flex flex-row items-end justify-between">
          <DialogTitle className="justify-between text-xl font-bold text-gray-950">
            <div>{title ?? "M'abonner à l'offre Viastud"}</div>
          </DialogTitle>
          <DialogClose asChild>
            <Button variant="none" className="m-0 h-6 w-6 justify-start p-0" onClick={onClose}>
              <X className="h-6 w-6" />
            </Button>
          </DialogClose>
        </DialogHeader>
        {!!clientSecret && (
          <Elements
            options={{
              clientSecret: clientSecret,
              appearance: {
                theme: 'flat',
                variables: {
                  colorPrimary: '#3347FF',
                  colorBackground: '#EBF4FF',
                  colorText: '#334155',
                  colorDanger: '#df1b41',
                  borderRadius: '16px',
                },
              },
              loader: 'auto',
            }}
            stripe={getStripe()}
          >
            <PaymentForm returnUrl={returnUrl} gtmEventType="achat_cours_unite" />
          </Elements>
        )}
      </DialogContent>
    </Dialog>
  )
}
